/**
 * Redirect for the retired `/orgs/:orgId/settings` route (#547 PR 2).
 *
 * Old path served a standalone OrgSettings page outside the
 * workspace context. The new unified Settings page lives inside
 * the workspace context at `/workspaces/:wsId/settings`, with the
 * Organization tabs as a section in its sectioned left rail.
 *
 * Redirect target: the earliest-created workspace under `:orgId`
 * on the Organization · General tab — same default rule the
 * OrgWorkspacePicker uses for "click an org → land on a
 * workspace." Falls back to `/onboarding` if the user has no
 * workspaces under that org at all (stale-link / revoked-access
 * edge case). `/account` USED to be the fallback but it is now
 * itself a redirect to a workspace-scoped settings URL — bouncing
 * there from here would either re-loop or land somewhere unrelated
 * to the requested org. Onboarding is the honest "you have nowhere
 * meaningful to go right now" destination.
 *
 * **Why not "currently active workspace if under :orgId"?**
 * This component is rendered OUTSIDE `WithWorkspaceContext`
 * (the route is at the identity tier — see App.tsx), so it has
 * no access to `useAppContext` and can't read the active
 * workspace. The "earliest" rule is what we can express purely
 * from identity + the workspaces fetch, and it matches the
 * picker's default-workspace behavior so users land in the same
 * place they would by clicking the org in the picker.
 */
import { useEffect, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useIdentity } from '@/hooks/useIdentity'
import { workspacesAPI, type WorkspaceSummary } from '@/lib/api'

export function OrgSettingsRedirect() {
  const { orgId } = useParams<{ orgId: string }>()
  const { state } = useIdentity()
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[] | null>(null)

  useEffect(() => {
    void workspacesAPI.list().then(setWorkspaces).catch(() => setWorkspaces([]))
  }, [])

  if (state.status !== 'ready' || workspaces === null) {
    // Identity / workspace list still loading — render nothing.
    // App.tsx's global loading state already covers the identity
    // case; workspaces loads quickly so a brief flash of nothing
    // is fine here.
    return null
  }

  // Find an active-workspace-under-this-org fallback. Sort by
  // createdAt ascending so the "earliest" workspace wins — matches
  // the picker's default-workspace rule (Phase 5b §3.2).
  const inOrg = workspaces
    .filter((w) => w.orgId === orgId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))

  if (inOrg.length === 0) {
    // User has no workspaces under this org. Could be a stale
    // deep-link (their access was revoked) or a typo in the URL.
    // /onboarding handles both the "no orgs at all" + "no workspace
    // I can reach" cases gracefully — the IdentityGate is the
    // backstop for the former.
    return <Navigate to="/onboarding" replace />
  }

  // Path-based deep link to the Organization · General tab. The
  // legacy `?tab=organization.general` shape would also work today
  // (the Settings index page back-compat-redirects it), but emitting
  // the new shape direct keeps the redirect a single hop.
  return (
    <Navigate
      to={`/workspaces/${inOrg[0]._id}/settings/organization/general`}
      replace
    />
  )
}
